'use client';

import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { getConsent, setConsent, logConsentAction, type ConsentRegion } from '@/lib/consent';

interface ConsentPreferenceCenterProps {
  region: ConsentRegion;
  onClose: () => void;
  onSave: () => void;
}

export default function ConsentPreferenceCenter({
  region,
  onClose,
  onSave,
}: ConsentPreferenceCenterProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);

  const [choices, setChoices] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  // Load current consent on mount
  useEffect(() => {
    const currentConsent = getConsent();
    if (currentConsent) {
      setChoices(currentConsent.choices);
    }
  }, []);

  // Focus trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        if (e.shiftKey) {
          // Shift+Tab
          if (document.activeElement === firstFocusableRef.current) {
            e.preventDefault();
            lastFocusableRef.current?.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastFocusableRef.current) {
            e.preventDefault();
            firstFocusableRef.current?.focus();
          }
        }
      }
    };

    dialogRef.current?.addEventListener('keydown', handleKeyDown);
    firstFocusableRef.current?.focus();

    return () => {
      dialogRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleToggle = (category: keyof typeof choices) => {
    if (category === 'necessary') return; // Cannot toggle necessary
    setChoices((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    setConsent(choices, region);
    logConsentAction('save_preferences', region, choices);
    onSave();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setChoices(allAccepted);
    setConsent(allAccepted, region);
    logConsentAction('accept_all', region, allAccepted);
    onSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 id="preferences-title" className="text-lg font-semibold text-gray-900">
            Preferências de Cookies
          </h2>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Necessary */}
          <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Cookies Necessários</h3>
              <p className="text-sm text-gray-600 mt-1">
                Essenciais para o funcionamento do site. Não podem ser desativados.
              </p>
            </div>
            <button
              role="switch"
              aria-checked={choices.necessary}
              disabled
              className="ml-2 w-12 h-6 bg-blue-600 rounded-full flex items-center justify-end px-1 cursor-not-allowed"
            >
              <div className="w-5 h-5 bg-white rounded-full" />
            </button>
          </div>

          {/* Analytics */}
          <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Cookies de Análise</h3>
              <p className="text-sm text-gray-600 mt-1">
                Nos ajudam a entender como você usa o site para melhorar a experiência.
              </p>
            </div>
            <button
              role="switch"
              aria-checked={choices.analytics}
              onClick={() => handleToggle('analytics')}
              className={`ml-2 w-12 h-6 rounded-full flex items-center transition-colors ${
                choices.analytics ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'
              }`}
            >
              <div className="w-5 h-5 bg-white rounded-full" />
            </button>
          </div>

          {/* Marketing */}
          <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Cookies de Marketing</h3>
              <p className="text-sm text-gray-600 mt-1">
                Usados para rastrear visitantes em sites e exibir anúncios relevantes.
              </p>
            </div>
            <button
              role="switch"
              aria-checked={choices.marketing}
              onClick={() => handleToggle('marketing')}
              className={`ml-2 w-12 h-6 rounded-full flex items-center transition-colors ${
                choices.marketing ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'
              }`}
            >
              <div className="w-5 h-5 bg-white rounded-full" />
            </button>
          </div>

          {/* Preferences */}
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Cookies de Preferências</h3>
              <p className="text-sm text-gray-600 mt-1">
                Lembram suas escolhas para personalizar sua experiência.
              </p>
            </div>
            <button
              role="switch"
              aria-checked={choices.preferences}
              onClick={() => handleToggle('preferences')}
              className={`ml-2 w-12 h-6 rounded-full flex items-center transition-colors ${
                choices.preferences ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'
              }`}
            >
              <div className="w-5 h-5 bg-white rounded-full" />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Salvar Preferências
          </button>
          <button
            ref={lastFocusableRef}
            onClick={handleAcceptAll}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Aceitar Tudo
          </button>
        </div>
      </div>
    </div>
  );
}
